import { css } from "@emotion/react";
import React, { forwardRef } from "react";
import { ShimmerEffect } from "../ShimmerEffect";
import { useTheme } from "../theme";

export interface TextareaBaseProps extends React.ComponentPropsWithoutRef<"textarea"> {
  /**
   * 挿入される値がloading中であることを表します。
   */
  loading?: boolean;

  /**
   * field-sizingプロパティの値を変更します。
   * @experimental これは実験的な機能です。Chromeなど一部のブラウザでのみ動作します。
   * @default "fixed"
   */
  sizing?: "content" | "fixed";

  /**
   * 見かけ上入力欄に、指定されたインタラクション要素を追加します。
   */
  renderActions?: () => React.ReactNode;
}

export const TextareaBase = forwardRef<HTMLTextAreaElement, TextareaBaseProps>(
  (
    { loading, sizing = "fixed", renderActions: Actions, rows, ...props },
    ref,
  ) => {
    const theme = useTheme();
    return (
      <ShimmerEffect active={loading}>
        <div
          css={css`
            display: flex;
            gap: 8px;
            padding: 8px 12px;
            background-color: white;
            border: 1px solid ${theme.palette.text.secondary};
            border-radius: 4px;
            transition: all 0.3s;
            &:has(textarea:focus) {
              border-color: ${theme.palette.intentions.primary.main};
            }
            &:has(textarea[data-touched="true"]:invalid) {
              border-color: ${theme.palette.intentions.error.main};
            }
            &:has(textarea:disabled) {
              background-color: ${theme.palette.action.disabledBackground};
              border-color: ${theme.palette.action.disabled};
            }
            &:has(textarea[readonly]) {
              background-color: ${theme.palette.action.readOnlyBackground};
              border-color: ${theme.palette.action.readOnly};
            }
          `}
        >
          <textarea
            rows={rows}
            css={[
              theme.typography.body,
              {
                fieldSizing: sizing,
                /**
                 * field-sizing: contentの場合でも、rowsが機能するようにするためにmin-heightを指定している。
                 */
                minHeight: rows !== undefined ? `${rows}lh` : undefined,
              },
              css`
                box-sizing: border-box;
                display: block;
                width: 100%;
                /**
              * この要素がGridアイテムかつfield-sizingがcontentの場合、width: 100%が効かなくなる場合がある。
              * 本質的には親側でケアされるべきだが、困難だと思われるため、こちら側でmin-width: 0pxを指定してこれを回避している。
              */
                min-width: 0px;
                color: ${theme.palette.text.primary};
                resize: none;
                outline: 0px;
                &::placeholder {
                  color: ${theme.palette.text.secondary};
                }
                &:disabled {
                  color: ${theme.palette.text.disabled};
                  cursor: not-allowed;
                }
                &[readonly] {
                  cursor: text;
                }
              `,
            ]}
            ref={ref}
            {...props}
          />
          {Actions && <Actions />}
        </div>
      </ShimmerEffect>
    );
  },
);
